import React from 'react';
import classNames from 'classnames';
import { s as styleInject } from '../style-inject.es-746bb8ed.js';
import { SettingsSection, SettingsSeparator, Label, InfoText, SettingsPanelFooter, SettingsMobileHeader, SettingsPanelHeader, InfoIconWithPopover, Toggle, LabeledToggle } from 'wix-rich-content-ui-components';
import ControlsSection from './ControlsSection.js';
import { SETTINGS_MODAL } from '../dataHooks.js';

var css_248z = ".DQulG,.glob_fontElementMap{font-family:var(--ricos-font-family,unset)}.odElE{width:100%}[dir=ltr] .odElE{text-align:left}[dir=rtl] .odElE{text-align:right}.odElE .TdorM{box-sizing:border-box;height:calc(100% - 177px);overflow:scroll;position:absolute;top:77px;width:100%}.odElE .TdorM *,.odElE .TdorM :after,.odElE .TdorM :before{box-sizing:inherit}.odElE .TdorM input{box-sizing:border-box}@supports not (contain:inline-size){@media only screen and (max-width:640px){.odElE .TdorM{height:calc(100% - 50px);top:50px}}}@container (width < 640px){.odElE .TdorM{height:calc(100% - 50px);top:50px}}.KA7X4{display:block;height:1px;width:100%}[dir] .KA7X4{background-color:var(--ricos-settings-dividers-color,#efeff4);margin-top:16px}.YSlws{font-size:20px}[dir=ltr] .YSlws{padding:24px 0 0 24px}[dir=rtl] .YSlws{padding:24px 24px 0 0}[dir] .u8BOA{padding:0 24px}[dir=ltr] .-yXGu .u8BOA{padding-right:18px}[dir=rtl] .-yXGu .u8BOA{padding-left:18px}.BQ8tS{align-items:center;display:flex;gap:8px}[dir] .BQ8tS{padding-top:20px}@supports not (contain:inline-size){@media only screen and (max-width:640px){[dir] .BQ8tS{margin-bottom:12px}}}@container (width < 640px){[dir] .BQ8tS{margin-bottom:12px}}.BQ8tS svg{fill:var(--ricos-settings-icons-color,currentColor);color:var(--ricos-settings-icons-color,currentColor)}[dir] .BQ8tS svg{cursor:pointer}";
var styles = {"fontElementMap":"DQulG","videoSettings":"odElE","videoSettings_scroll_container":"TdorM","separator":"KA7X4","videoSettingsTitle":"YSlws","videoSettings_toggleContainer":"u8BOA","videoSettings_mobile":"-yXGu","videoSettings_labeledToggle_withPopover":"BQ8tS"};
styleInject(css_248z);

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
const VideoSettings = ({
  componentData,
  t,
  isMobile,
  settings,
  experiments = {},
  onSave,
  onCancel,
  updateData,
  shouldShowSpoiler,
  useAutoPlay
}) => {
  var _a, _b, _c, _d;
  const hasDisableDownload = componentData.disableDownload !== void 0 && componentData.disableDownload !== null;
  const isCustomVideo = hasDisableDownload || ((_b = (_a = componentData.video) == null ? void 0 : _a.src) == null ? void 0 : _b.id);
  const disableDownload = hasDisableDownload ? componentData.disableDownload : !!(settings == null ? void 0 : settings.disableDownload);
  const isSpoilered = (_c = componentData.containerData.spoiler) == null ? void 0 : _c.enabled;
  const onDoneClick = () => {
    return onSave == null ? void 0 : onSave();
  };
  const onCancelClick = () => onCancel == null ? void 0 : onCancel();
  const onControlsChange = (showControls) => {
    updateData == null ? void 0 : updateData({
      options: __spreadProps(__spreadValues({}, componentData.options), { showControls })
    });
  };
  const spoilerToggle = {
    toggleKey: "isSpoilerEnabled",
    labelKey: "VideoSettings_Spoiler_Toggle",
    dataHook: SETTINGS_MODAL.SPOILER_TOGGLE,
    tooltipText: "Spoiler_Toggle_Tooltip",
    checked: isSpoilered,
    onToggle: () => {
      updateData == null ? void 0 : updateData({
        containerData: __spreadProps(__spreadValues({}, componentData.containerData), { spoiler: { enabled: !isSpoilered } })
      });
    }
  };
  const downloadToggle = {
    toggleKey: "isDownloadEnabled",
    labelKey: "VideoPlugin_Settings_VideoCanBeDownloaded_Label",
    dataHook: SETTINGS_MODAL.ALLOW_DOWNALOD_TOGGLE,
    tooltipText: "VideoPlugin_Settings_VideoCanBeDownloaded_Tooltip",
    checked: !disableDownload,
    onToggle: () => updateData == null ? void 0 : updateData({ disableDownload: !disableDownload })
  };
  const getVideoOptionToggles = () => {
    const { autoPlay = false, playInLoop = false } = (componentData == null ? void 0 : componentData.options) || {};
    return [
      {
        toggleKey: "autoPlay",
        labelKey: "Media_Autoplay_Label",
        dataHook: "videoAutoPlay",
        tooltipText: "VideoSettings_Autoplay_Tooltip",
        checked: autoPlay,
        onToggle: () => updateData == null ? void 0 : updateData({ options: __spreadProps(__spreadValues({}, componentData.options), { autoPlay: !autoPlay }) })
      },
      {
        toggleKey: "playInLoop",
        labelKey: "Media_Play_In_Loop_Label",
        dataHook: "videoPlayInLoop",
        checked: playInLoop,
        onToggle: () => updateData == null ? void 0 : updateData({ options: __spreadProps(__spreadValues({}, componentData.options), { playInLoop: !playInLoop }) })
      }
    ];
  };
  const renderHeader = () => isMobile ? /* @__PURE__ */ React.createElement(
    SettingsMobileHeader,
    {
      t,
      onCancel: onCancelClick,
      onSave: onDoneClick,
      title: t("VideoModal_MobileHeader")
    }
  ) : /* @__PURE__ */ React.createElement(SettingsPanelHeader, { title: t("VideoPlugin_Settings_Header"), onClose: onCancelClick });
  const toggleData = isCustomVideo ? [downloadToggle] : [];
  shouldShowSpoiler && toggleData.push(spoilerToggle);
  useAutoPlay && toggleData.push(...getVideoOptionToggles());
  const toggleKeysMap = toggleData.reduce((res, { toggleKey }) => {
    return __spreadProps(__spreadValues({}, res), {
      [toggleKey]: toggleKey
    });
  }, {});
  const renderToggleByKey = (toggleKey) => {
    const toggle = toggleData.find((toggle2) => toggle2.toggleKey === toggleKey);
    if (toggle) {
      const props = {
        onChange: toggle.onToggle,
        checked: toggle.checked,
        label: t(toggle.labelKey),
        dataHook: toggle.dataHook
      };
      return /* @__PURE__ */ React.createElement(React.Fragment, null, useAutoPlay ? (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        /* @__PURE__ */ React.createElement(
          "div",
          {
            key: toggle.toggleKey,
            className: styles.videoSettings_labeledToggle_withPopover,
            tabIndex: 0,
            onKeyPress: (e) => {
              if (e.key === "Enter") {
                props.onChange();
              }
            }
          },
          /* @__PURE__ */ React.createElement(
            Label,
            {
              label: t(props.label),
              style: isMobile ? { fontSize: 14 } : { fontSize: 16 }
            }
          ),
          !isMobile && t(toggle.tooltipText) && /* @__PURE__ */ React.createElement(InfoIconWithPopover, null, /* @__PURE__ */ React.createElement(React.Fragment, null, t(toggle.tooltipText))),
          /* @__PURE__ */ React.createElement(Toggle, __spreadValues({}, props))
        )
      ) : /* @__PURE__ */ React.createElement(
        LabeledToggle,
        __spreadProps(__spreadValues({
          key: toggle.toggleKey
        }, props), {
          tooltipText: !isMobile ? t(toggle.tooltipText) : void 0,
          style: isMobile ? { paddingBottom: 12 } : {}
        })
      ), isMobile && /* @__PURE__ */ React.createElement(InfoText, null, t(toggle.tooltipText)));
    }
  };
  const renderToggles = () => toggleData.map(({ toggleKey }) => renderToggleByKey(toggleKey));
  return /* @__PURE__ */ React.createElement(
    "div",
    {
      "data-hook": "settings",
      className: classNames(styles.videoSettings, {
        [styles.videoSettings_mobile]: isMobile
      })
    },
    /* @__PURE__ */ React.createElement(
      SettingsSection,
      {
        className: classNames(
          styles.videoSettings_toggleContainer,
          styles.videoSettings_scroll_container
        )
      },
      !useAutoPlay ? renderToggles() : /* @__PURE__ */ React.createElement(React.Fragment, null, toggleKeysMap.isDownloadEnabled && /* @__PURE__ */ React.createElement(React.Fragment, null, renderToggleByKey(toggleKeysMap.isDownloadEnabled), /* @__PURE__ */ React.createElement(SettingsSeparator, { top: true, bottom: true })), /* @__PURE__ */ React.createElement(
        Label,
        {
          label: t("Media_Playback_Options_Label"),
          style: { fontSize: 16, paddingTop: !isCustomVideo ? 24 : 0 }
        }
      ), renderToggleByKey(toggleKeysMap.autoPlay), renderToggleByKey(toggleKeysMap.playInLoop), /* @__PURE__ */ React.createElement(SettingsSeparator, { top: true, bottom: true }), /* @__PURE__ */ React.createElement(
        ControlsSection,
        {
          t,
          showControls: (_d = componentData.options) == null ? void 0 : _d.showControls,
          onChange: onControlsChange
        }
      ), !isCustomVideo && /* @__PURE__ */ React.createElement(InfoText, null, t("VideoSettings_Embed_Videos_Alert"), " "), toggleKeysMap.isSpoilerEnabled && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(SettingsSeparator, { top: true }), renderToggleByKey(toggleKeysMap.isSpoilerEnabled)))
    ),
    !isMobile && /* @__PURE__ */ React.createElement(SettingsPanelFooter, { fixed: true, cancel: onCancelClick, save: onDoneClick, t }),
    renderHeader()
  );
};

export { VideoSettings as default };
//# sourceMappingURL=VideoSettings.js.map
